import React from "react";
import { IconButton, Switch, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import DescriptionIcon from "@mui/icons-material/Description";
import ConstructionIcon from "@mui/icons-material/Construction";
import WeekendIcon from "@mui/icons-material/Weekend";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import DocumentStatusIndicator from "../../indicators/DocumentStatusIndicator";
import { useDocumentationAnalysis } from "../../../../hooks/useDocumentationAnalysis";

const getIconComponent = (iconName) => {
  switch (iconName) {
    case 'description': return <DescriptionIcon />;
    case 'warning': return <WarningIcon />;
    case 'construction': return <ConstructionIcon />;
    case 'weekend': return <WeekendIcon />;
    case 'compare_arrows': return <CompareArrowsIcon />;
    case 'do_not_disturb': return <DoNotDisturbIcon />;
    default: return <WarningIcon />;
  }
};

const InactiveReason = ({ theme, reason }) => {
  if (!reason) return null;
  
  return (
    <Tooltip title={
      <div>
        <h4>{reason.label}</h4>
        {reason.details && <p>{reason.details}</p>}
        {reason.blocksActivation && 
          <p className="warning-text">Este problema impide activar el vehículo</p>
        }
      </div>
    }>
      <div className={`GM__${theme}-inactive-reason ${reason.type}`}>
        {getIconComponent(reason.icon)}
        <span>{reason.label}</span>
      </div>
    </Tooltip>
  );
};

const Pagination = ({ theme, currentPage, totalPages, onPageChange }) => (
  <div className={`GM__${theme}-pagination`}>
    <button
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage === 1}
      className={`GM__${theme}-pagination-btn`}
    >
      &lt;
    </button>
    <span className={`GM__${theme}-pagination-info`}>
      Página {currentPage} de {totalPages}
    </span>
    <button
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
      className={`GM__${theme}-pagination-btn`}
    >
      &gt;
    </button>
  </div>
);

const CombinedVehicleItemWithDocStatus = ({ 
  theme, 
  combo, 
  onStatusChange, 
  onViewDetails, 
  onRemoveFromRoute,
  getInactiveReason,
  selectedRouteId,
  operationName
}) => {
  const vehicleDocAnalysis = useDocumentationAnalysis(combo.vehicle);
  const trailerDocAnalysis = useDocumentationAnalysis({
    vehicleDocuments: combo.trailerDocuments,
    operatorDocuments: [],
  });
  const handleRemoveConfirmation = () => {
    const routeName = combo.routeInfo?.routeName || 'la ruta actual';
    const vehicleInfo = `${combo.vehicle?.plate} - ${combo.trailer?.plate}`;
    const confirmMessage = `¿Desea eliminar la combinación ${vehicleInfo} de ${routeName} que pertenece a la operación ${operationName}?`;
    
    if (window.confirm(confirmMessage)) {
      onRemoveFromRoute(combo.relation?.id, vehicleInfo, routeName);
    }
  };
  const blocksActivation = vehicleDocAnalysis?.blocksActivation || trailerDocAnalysis?.blocksActivation;
  const isActive = combo.relation?.vehicle_relation_active && combo.relation?.trailer_relation_active;

  return (
    <div
      key={combo.id}
      className={`GM__${theme}-vehicle-item combined ${
        !isActive ? 'inactive' : ''
      } ${blocksActivation ? 'documentation-blocked' : ''}`}
    >
      <div className={`GM__${theme}-vehicle-plate`}>
        <div className={`GM__${theme}-combined-vehicle-info`}>
          {combo.vehicle?.plate || "N/A"}
          {vehicleDocAnalysis && (
            <DocumentStatusIndicator
              status={vehicleDocAnalysis.status}
              theme={theme}
              size="small"
              details={vehicleDocAnalysis.details?.vehicle?.details}
            />
          )}
        </div>
      </div>

      <div className={`GM__${theme}-vehicle-plate`}>
        <div className={`GM__${theme}-combined-trailer-info`}>
          {combo.trailer?.plate || "N/A"}
          {trailerDocAnalysis && (
            <DocumentStatusIndicator
              status={trailerDocAnalysis.status}
              theme={theme}
              size="small"
              details={trailerDocAnalysis.details?.vehicle?.details}
            />
          )}
        </div>
      </div>

      <div className={`GM__${theme}-vehicle-status`}>
        <Switch
          checked={isActive}
          onChange={(e) => onStatusChange(combo, e)}
          color="primary"
          disabled={blocksActivation}
        />
      </div>

      <div className={`GM__${theme}-vehicle-reason`}>
        {blocksActivation ? (
          <Tooltip title="Documentos vencidos en vehículo o remolque">
            <span className={`GM__${theme}-documentation-block`}>
              Documentos vencidos
            </span>
          </Tooltip>
        ) : !isActive ? (
          <InactiveReason 
            theme={theme} 
            reason={getInactiveReason(combo.vehicle)} 
          />
        ) : null}
      </div>

      <div className={`GM__${theme}-vehicle-actions`}>
        <IconButton
          className={`GM__${theme}-view-button`}
          size="small"
          onClick={() => onViewDetails(combo)}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
        {selectedRouteId && (
          <IconButton
            className={`GM__${theme}-delete-button`}
            size="small"
            onClick={handleRemoveConfirmation}
            title="Eliminar combinación de la ruta"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </div>
    </div>
  );
};

const CombinedVehiclesList = ({
  theme,
  combinedVehicles,
  totalPages,
  currentPage,
  onPageChange,
  onStatusChange,
  onViewDetails,
  onRemoveFromRoute,
  getInactiveReason,
  selectedRouteId,
  operationName
}) => {
  if (combinedVehicles.length === 0) return null;

  return (
    <div className={`GM__${theme}-combined-section`}>
      <div className={`GM__${theme}-section-title`}>
        Combinaciones
      </div>
      <div className={`GM__${theme}-vehicles-list-header`}>
        <div className={`GM__${theme}-vehicle-plate-header`}>
          Cabezote
        </div>
        <div className={`GM__${theme}-vehicle-plate-header`}>
          Remolque
        </div>
        <div className={`GM__${theme}-vehicle-status-header`}>
          Estado
        </div>
        <div className={`GM__${theme}-vehicle-reason-header`}>
          Estado/Razón
        </div>
        <div className={`GM__${theme}-vehicle-actions-header`}>
          Acciones
        </div>
      </div>

      {combinedVehicles.map((combo) => (
        <CombinedVehicleItemWithDocStatus
          key={combo.id}
          theme={theme}
          combo={combo}
          onStatusChange={onStatusChange}
          onViewDetails={onViewDetails}
          onRemoveFromRoute={onRemoveFromRoute}
          getInactiveReason={getInactiveReason}
          selectedRouteId={selectedRouteId}
          operationName={operationName}
        />
      ))}

      {/* Paginación */}
      {totalPages > 1 && (
        <Pagination 
          theme={theme}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};


export default React.memo(CombinedVehiclesList);